//
// Copyright (c) 2011-2022 by Curalate, Inc.
//
import _ from 'underscore';

import { IDraftInstagramPost } from '@apis/universal-posts/draft-posts/universalDraftPostsApiModel';
import { IScheduledInstagramPost } from '@apis/universal-posts/scheduled-posts/universalScheduledPostsApiModel';
import { getInstagramStandardPostDetails } from '@instagram-account/redux/schedule/instagramScheduleUtils';
import { IMlsMediaItem } from '@models/IMlsMediaItem';
import { isDefined } from '@utils/filterPredicates';
import { reduceToRecord } from '@utils/reduceToRecord';

import logger from 'app/logger/logger';

export const extractScheduledItemMediaArr = (
  item: IScheduledInstagramPost | IDraftInstagramPost,
): IMlsMediaItem[] => {
  const id =
    (item as IScheduledInstagramPost).id || (item as IDraftInstagramPost).id;
  const allMedia: IMlsMediaItem[] = [];
  const { carouselMedia, previewMediaItem } =
    getInstagramStandardPostDetails(item);
  if (previewMediaItem) {
    allMedia.push(previewMediaItem);
  } else {
    logger.console.error(`Base MediaItem missing for item #${id}`);
    return [];
  }

  if (carouselMedia) {
    allMedia.push(
      ...carouselMedia
        .map((c) => {
          if (c.mediaItem) {
            return c.mediaItem;
          } else {
            logger.console.error(
              `Carousel MediaItem missing for item #${id}, carouselMedia w/ cuid ${c.cuid}`,
            );
          }
        })
        .filter(isDefined),
    );
  }

  return allMedia;
};

/*
 * Prepares a scheduled or draft item for storage by normalizing all of the item's associated media items
 */
export const extractScheduledItemMedia = (
  item: IScheduledInstagramPost | IDraftInstagramPost,
): Record<string, IMlsMediaItem> => {
  const allMedia = extractScheduledItemMediaArr(item);
  return reduceToRecord(allMedia, (media) => [media.cuid, media]);
};

export const extractAllScheduledFeedItemMedia = (
  items: (IScheduledInstagramPost | IDraftInstagramPost)[],
): Record<string, IMlsMediaItem> => {
  return Object.assign({}, ...items.map(extractScheduledItemMedia));
};

export const extractAllUniversalScheduledPostMedia = (
  items: (IScheduledInstagramPost | IDraftInstagramPost)[],
): Record<string, IMlsMediaItem> => {
  return Object.assign({}, ...items.map((post) => post.mediaItems));
};
